import { useMemo } from 'react';
import { partition } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import { SellerRole } from '../../../gql-operations/gql-generated';
import { mapSkills } from '../../utils/mapSkills';
import { useOrderedSkills } from '../../hooks/useOrderedSkills';
import { mapServices } from '../../utils/mapServices';
import { PortfolioProject } from '../../types';
import { ITEM_ALL_ID } from './consts';

const getProjectsCount = (id: string, portfolioProjects: PortfolioProject[], key: 'services' | 'metadata') =>
    portfolioProjects.filter((project) =>
        project.portfolioProjectRoleCatalog[key]?.some((item: { id: string }) => item.id === id)
    ).length;

export const useServicesAndSkills = (portfolioProjects: PortfolioProject[], sellerRole?: SellerRole | null) => {
    const items = useMemo(() => {
        const services = mapServices(sellerRole?.services || []).map((service) => ({
            ...service,
            count: getProjectsCount(service.id, portfolioProjects, 'services'),
        }));

        const skills = mapSkills(sellerRole?.proficient || []).map((skill) => ({
            ...skill,
            count: getProjectsCount(skill.id, portfolioProjects, 'metadata'),
        }));

        return [...services, ...skills];
    }, [sellerRole, portfolioProjects]);

    const orderedItems = useOrderedSkills(items, sellerRole?.id);

    return useMemo(() => {
        const [itemsWithCount, itemsWithoutCount] = partition(orderedItems, (item) => item.count);

        const itemAll = {
            id: ITEM_ALL_ID,
            name: translate('seller_page_perseus.services_tags.all'),
        };

        return [itemAll, ...itemsWithCount, ...itemsWithoutCount];
    }, [orderedItems]);
};

export const usePortfolioProjectsByRole = (portfolioProjects: PortfolioProject[], sellerRole?: SellerRole | null) =>
    useMemo(
        () =>
            portfolioProjects.filter((portfolio) => {
                const portfolioRoleIds = portfolio.portfolioProjectRoleCatalog.sellerRoles.map((role) => role.id);
                return sellerRole?.id && portfolioRoleIds.includes(sellerRole?.id);
            }),
        [portfolioProjects, sellerRole]
    );
